import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  CheckCircle2,
  Search,
  FileText,
  Camera,
  AlertTriangle,
} from "lucide-react";
import { SEO } from "@/components/SEO";
import { ServiceAreasSection } from "@/components/CrossLinks";
import { inspectionsSchema } from "./schemas/inspectionsSchema";

export default function Inspections() {
  return (
    <div className="flex flex-col min-h-screen">
      <SEO
        title="Roof Inspection Saskatchewan | Sask Roofing"
        description="Get a free, professional roof inspection in Saskatchewan. We identify hidden damage, assess ventilation, and provide honest, no-pressure recommendations."
        schema={inspectionsSchema}
      />

      {/* Hero */}
      <section className="relative py-32 text-primary-foreground overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://wsrv.nl/?url=https://vibe.filesafe.space/1783106672919603654/assets/2de89809-eea4-46a6-aaf9-fe65daf5aa25.png&output=webp&q=60"
            alt="Professional Roof Inspection"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black/40"></div>
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent"></div>
        </div>
        <div className="container relative px-4 z-10">
          <div className="max-w-3xl space-y-6">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-white drop-shadow-md">
              Roof Inspections Saskatchewan
            </h1>
            <p className="text-xl text-white/90 drop-shadow-sm font-medium">
              Know exactly what's happening above your head. We provide
              thorough, honest roof inspections to catch small problems before
              they become expensive disasters.
            </p>
            <div className="pt-4 flex flex-col sm:flex-row gap-4">
              <Button
                size="lg"
                className="bg-accent text-accent-foreground hover:bg-accent/90 shadow-lg"
                asChild
              >
                <Link to="/contact">Schedule a Free Inspection</Link>
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="bg-transparent text-white border-white hover:bg-white/10 shadow-lg"
                asChild
              >
                <a href="tel:6397397969">Call Now (639) 739-7969</a>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* When you need it & Warning Signs */}
      <section className="py-20 bg-background">
        <div className="container px-4">
          <div className="grid md:grid-cols-2 gap-12">
            <div className="space-y-6">
              <h2 className="text-3xl font-bold">
                When You Need a Roof Inspection
              </h2>
              <p className="text-lg text-muted-foreground leading-relaxed">
                Most homeowners only think about their roof when water starts
                dripping from the ceiling. A proactive inspection can save you
                thousands of dollars in interior water damage, drywall
                replacement, and mold remediation.
              </p>

              <div className="space-y-4 my-8">
                <h3 className="text-xl font-bold text-foreground flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-accent" />4 Reasons to
                  Get Inspected Now
                </h3>
                <ul className="space-y-4">
                  <li className="flex gap-4">
                    <span className="text-3xl font-bold text-primary/20 leading-none">
                      01
                    </span>
                    <div>
                      <strong className="text-foreground block">
                        After a Major Storm
                      </strong>
                      <span className="text-muted-foreground">
                        High winds and hail can compromise your roof without
                        leaving obvious signs from the ground.
                      </span>
                    </div>
                  </li>
                  <li className="flex gap-4">
                    <span className="text-3xl font-bold text-primary/20 leading-none">
                      02
                    </span>
                    <div>
                      <strong className="text-foreground block">
                        Buying or Selling a Home
                      </strong>
                      <span className="text-muted-foreground">
                        Ensure there are no hidden surprises that could derail a
                        real estate transaction.
                      </span>
                    </div>
                  </li>
                  <li className="flex gap-4">
                    <span className="text-3xl font-bold text-primary/20 leading-none">
                      03
                    </span>
                    <div>
                      <strong className="text-foreground block">
                        Roof is Over 15 Years Old
                      </strong>
                      <span className="text-muted-foreground">
                        Asphalt shingles degrade over time. Knowing their exact
                        condition helps you plan financially.
                      </span>
                    </div>
                  </li>
                  <li className="flex gap-4">
                    <span className="text-3xl font-bold text-primary/20 leading-none">
                      04
                    </span>
                    <div>
                      <strong className="text-foreground block">
                        High Energy Bills
                      </strong>
                      <span className="text-muted-foreground">
                        Poor attic ventilation forces your HVAC to work harder.
                        We inspect intake and exhaust balance.
                      </span>
                    </div>
                  </li>
                </ul>
              </div>
            </div>

            <div className="space-y-8">
              <div className="bg-muted/30 p-8 rounded-xl border">
                <h3 className="text-2xl font-bold mb-6">
                  Why It Suits Saskatchewan
                </h3>
                <ul className="space-y-4">
                  {[
                    "We specifically look for signs of ice damming and winter condensation.",
                    "We check for wind-lifted shingles common after prairie gales.",
                    "We identify subtle hail bruising that untrained eyes miss.",
                    "Honest, local advice—we won't sell you a roof you don't need.",
                  ].map((item, i) => (
                    <li key={i} className="flex gap-3 text-muted-foreground">
                      <CheckCircle2 className="w-6 h-6 text-primary shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="relative rounded-2xl overflow-hidden shadow-xl h-64">
                <img
                  src="https://wsrv.nl/?url=https://vibe.filesafe.space/1783106672919603654/assets/2de89809-eea4-46a6-aaf9-fe65daf5aa25.png&output=webp&q=60"
                  alt="Drone Roof Inspection"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Materials We Use (Technology) */}
      <section className="py-20 bg-muted/30 border-y">
        <div className="container px-4">
          <div className="max-w-3xl mx-auto space-y-6 text-center">
            <h2 className="text-3xl font-bold">
              Technology We Use for Inspections
            </h2>
            <p className="text-lg text-muted-foreground leading-relaxed">
              We leverage modern technology alongside decades of hands-on
              experience to provide the most accurate assessment possible. For
              steep or complex roofs, we utilize high-resolution drones to
              capture detailed imagery of every ridge and valley without risking
              damage to brittle shingles. We also use moisture detection tools
              inside your attic to pinpoint the exact source of condensation or
              hidden leaks, ensuring our diagnosis is based on hard data, not
              just guesswork.
            </p>
          </div>
        </div>
      </section>

      {/* Cost */}
      <section className="py-20 bg-background">
        <div className="container px-4">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <h2 className="text-3xl font-bold">What It Costs</h2>

              <div className="bg-primary/5 border border-primary/20 p-6 rounded-xl">
                <h3 className="font-bold text-xl mb-2 text-foreground">
                  Standard Inspection: $0 (Free)
                </h3>
                <p className="text-sm text-muted-foreground italic">
                  *Our standard residential roof inspections and replacement
                  estimates are completely free.
                </p>
              </div>

              <p className="text-lg text-muted-foreground leading-relaxed">
                We believe in earning your trust first. That's why our standard
                inspections are free. If you require a formal, written
                inspection report for a real estate transaction, insurance
                dispute, or mortgage requirement, there is a nominal fee
                (typically $150 - $300). However, if that inspection leads to a
                repair or replacement booked with us, we fully credit that fee
                toward your project cost. You can learn more about general
                roofing costs in our{" "}
                <Link
                  to="/roofing-cost-guide-saskatchewan"
                  className="text-accent hover:underline"
                >
                  Saskatchewan Roofing Cost Guide
                </Link>
                .
              </p>
            </div>

            <div className="relative rounded-2xl overflow-hidden shadow-xl h-80">
              <img
                src="https://wsrv.nl/?url=https://vibe.filesafe.space/1783106672919603654/assets/2de89809-eea4-46a6-aaf9-fe65daf5aa25.png&output=webp&q=60"
                alt="Attic Ventilation Inspection"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Process Timeline */}
      <section className="py-20 bg-muted/30 border-y">
        <div className="container px-4">
          <div className="text-center max-w-3xl mx-auto mb-16 space-y-4">
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-foreground">
              Our Inspection Process
            </h2>
            <p className="text-lg text-muted-foreground">
              A comprehensive 3-step check of your entire roofing system.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8 relative max-w-4xl mx-auto">
            <div className="hidden md:block absolute top-8 left-[16%] right-[16%] h-0.5 bg-border -z-10"></div>

            <div className="relative space-y-4 bg-muted/30 pt-2 text-center">
              <div className="w-16 h-16 bg-primary text-primary-foreground rounded-full flex items-center justify-center border-4 border-muted/30 mx-auto shadow-sm">
                <Search className="w-6 h-6 text-accent" />
              </div>
              <h3 className="text-xl font-bold">1. Exterior Assessment</h3>
              <p className="text-muted-foreground">
                We physically examine the shingles, flashings, valleys, and
                eavestroughs for wear, damage, or poor installation.
              </p>
            </div>

            <div className="relative space-y-4 bg-muted/30 pt-2 text-center">
              <div className="w-16 h-16 bg-primary text-primary-foreground rounded-full flex items-center justify-center border-4 border-muted/30 mx-auto shadow-sm">
                <Camera className="w-6 h-6 text-accent" />
              </div>
              <h3 className="text-xl font-bold">2. Attic & Ventilation</h3>
              <p className="text-muted-foreground">
                We check inside your attic for proper insulation levels,
                moisture buildup, and balanced intake/exhaust airflow.
              </p>
            </div>

            <div className="relative space-y-4 bg-muted/30 pt-2 text-center">
              <div className="w-16 h-16 bg-primary text-primary-foreground rounded-full flex items-center justify-center border-4 border-muted/30 mx-auto shadow-sm">
                <FileText className="w-6 h-6 text-accent" />
              </div>
              <h3 className="text-xl font-bold">3. Honest Review</h3>
              <p className="text-muted-foreground">
                We sit down with you, share the photo documentation, and explain
                the health of your roof with zero sales pressure.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Service Areas */}
      <ServiceAreasSection service="Roof Inspection" serviceSlug="inspection" />

      {/* FAQ */}
      <section className="py-20 bg-background border-y">
        <div className="container px-4 max-w-4xl">
          <h2 className="text-3xl font-bold text-center mb-12">
            Roof Inspection FAQ
          </h2>
          <Accordion type="single" collapsible className="w-full">
            <AccordionItem value="item-1">
              <AccordionTrigger className="text-lg font-medium text-left">
                How often should I have my roof inspected?
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground text-base">
                We recommend a professional inspection every 3-5 years for roofs
                over 10 years old, or immediately following a severe wind or
                hail storm.
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="item-2">
              <AccordionTrigger className="text-lg font-medium text-left">
                Are your roof inspections really free?
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground text-base">
                Yes, our standard residential roof inspections and replacement
                estimates are completely free and carry no obligation.
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="item-3">
              <AccordionTrigger className="text-lg font-medium text-left">
                Do I need to be home for the inspection?
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground text-base">
                It's preferred so we can discuss our findings with you, but not
                strictly necessary for an exterior inspection. We can always
                email you the photos and report and discuss it over the phone.
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </section>

      {/* Cross Links & CTA Banner */}
      <section className="py-20 bg-primary text-primary-foreground text-center">
        <div className="container px-4 max-w-4xl space-y-8">
          <h2 className="text-3xl md:text-4xl font-bold">
            Know the Health of Your Roof
          </h2>
          <p className="text-xl text-primary-foreground/90 leading-relaxed">
            We serve homeowners across Saskatoon, Regina, Moose Jaw, Prince
            Albert, Swift Current, Yorkton, Estevan, and Weyburn. A standard
            inspection takes less than an hour.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4 pt-4">
            <Button
              size="lg"
              className="bg-accent text-accent-foreground hover:bg-accent/90 text-lg px-8 py-6 h-auto"
              asChild
            >
              <Link to="/contact">Book Your Free Inspection</Link>
            </Button>
            <Button
              size="lg"
              variant="outline"
              className="text-lg px-8 py-6 h-auto bg-transparent border-white text-white hover:bg-white/10"
              asChild
            >
              <Link to="/services/repair">Explore Repair Services</Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}
